/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 */

import React, { useState } from 'react';
import './Footer.css';

const Icons = {
    HelpCircle: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 16v-4"></path><path d="M12 8h.01"></path></svg>,
    BookOpen: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>,
    MessageCircle: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>,
    AlertCircle: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>,
    Mail: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"></rect><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path></svg>,
    WhatsApp: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>,
    X: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
};

export default function Footer() {
    const [showMenu, setShowMenu] = useState(false);
    const [showContactForm, setShowContactForm] = useState(false);
    const [contactForm, setContactForm] = useState({ email: '', message: '' });
    const [contactSubmitted, setContactSubmitted] = useState(false);

    const toggleMenu = () => setShowMenu(!showMenu);

    const handleContactChange = (e) => {
        const { name, value } = e.target;
        setContactForm(prev => ({ ...prev, [name]: value }));
    };

    const handleContactSubmit = (e) => {
        e.preventDefault();
        // Send to support email or API
        const mailtoLink = `mailto:marksure.sa@gmail.com?subject=Marksure%20Support&body=${encodeURIComponent(
            `From: ${contactForm.email}\n\nMessage:\n${contactForm.message}`
        )}`;
        window.location.href = mailtoLink;
        
        setContactForm({ email: '', message: '' });
        setContactSubmitted(true);
        setTimeout(() => setContactSubmitted(false), 3000);
    };

    const openGuide = () => {
        window.open('https://marksureinfo.page/videos', '_blank');
    };

    const openWhatsApp = () => {
        // Format: https://wa.me/{phone_number}
        window.open('https://wa.me/27736531374', '_blank');
    };

    return (
        <footer className="app-footer">
            <div className="footer-content">
                <div className="footer-left">
                    <span className="footer-text">© 2025 Marksure. Built for schools.</span>
                </div>

                <div className="footer-right">
                    <div className="support-group">
                        <span className="support-label">Need support?</span>
                        <div className="help-buttons">
                            <button 
                                className="help-btn whatsapp-btn" 
                                onClick={openWhatsApp} 
                                title="Contact via WhatsApp"
                            >
                                <Icons.WhatsApp />
                            </button>
                            <div className="help-menu-container">
                                <button className="help-btn" onClick={toggleMenu} title="Help & Support">
                                    <Icons.HelpCircle />
                                </button>

                        {showMenu && (
                            <div className="help-menu">
                                <div className="menu-header">
                                    <span>Help & Support</span>
                                    <button className="menu-close" onClick={() => setShowMenu(false)}>
                                        <Icons.X />
                                    </button>
                                </div>

                                <div className="menu-items">
                                    <button 
                                        className="menu-item"
                                        onClick={() => { openGuide(); setShowMenu(false); }}
                                    >
                                        <Icons.BookOpen />
                                        <span>User Guides (bottom of page)</span>
                                    </button>

                                    <button 
                                        className="menu-item"
                                        onClick={() => { setShowContactForm(true); setShowMenu(false); }}
                                    >
                                        <Icons.Mail />
                                        <span>Contact Support</span>
                                    </button>

                                    <button 
                                        className="menu-item"
                                        onClick={() => alert('FAQs coming soon! Common issues:\n\n• CSV imports: Use the Excel template and match section names\n• Support alerts: These indicate at-risk learners, not final marks\n• Weights: Changes apply to new marks only')}
                                    >
                                        <Icons.AlertCircle />
                                        <span>FAQs</span>
                                    </button>
                                </div>
                            </div>
                        )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Contact Support Modal */}
            {showContactForm && (
                <div className="modal-overlay" onClick={() => setShowContactForm(false)}>
                    <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Contact Support</h3>
                            <button className="modal-close" onClick={() => setShowContactForm(false)}>
                                <Icons.X />
                            </button>
                        </div>

                        {contactSubmitted ? (
                            <div className="success-message">
                                <p>Thanks! Your message has been sent to marksure.sa@gmail.com</p>
                                <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '8px' }}>We typically respond within 24 hours.</p>
                            </div>
                        ) : (
                            <form onSubmit={handleContactSubmit}>
                                <div className="form-group">
                                    <label>Email Address</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={contactForm.email}
                                        onChange={handleContactChange}
                                        placeholder="your.email@school.edu"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Message</label>
                                    <textarea
                                        name="message"
                                        value={contactForm.message}
                                        onChange={handleContactChange}
                                        placeholder="Describe your issue or question..."
                                        rows="5"
                                        required
                                    ></textarea>
                                </div>

                                <div className="form-actions">
                                    <button type="button" className="btn-cancel" onClick={() => setShowContactForm(false)}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn-submit">
                                        Send
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </footer>
    );
}
